// Shared completion status helper for portfolios
// Computes completion based on allocation percentages with floating-point tolerance.

export interface HasAllocations {
  portfolio_size?: number | null;
  portfolio_allocations?: Array<{
    allocation_percentage: number | string | null;
    allocation_value?: number | string | null;
  }>;
}

// Tolerance aligned with UI rounding: treat within 0.01% as complete
const EPSILON_PERCENT = 0.01; // 1e-2 percent

export function isPortfolioCompleted(p: HasAllocations | null | undefined): boolean {
  if (!p || !Array.isArray(p.portfolio_allocations)) return false;
  const hasAnyAllocRow = p.portfolio_allocations.length > 0;
  if (!hasAnyAllocRow) return false;

  const size = typeof p.portfolio_size === 'number' ? p.portfolio_size : Number(p.portfolio_size);
  const hasValidSize = isFinite(size) && size > 0;

  // If we have values, compute based on values; else fallback to percentages
  const anyValueSpecified = p.portfolio_allocations.some(a => a && a.allocation_value != null && a.allocation_value !== '');

  if (hasValidSize && anyValueSpecified) {
    const totalVal = p.portfolio_allocations.reduce((sum, a) => {
      const val = a?.allocation_value;
      const numVal = typeof val === 'number' ? val : Number(val);
      if (isFinite(numVal)) return sum + Math.max(0, numVal);
      // fallback to percentage if value invalid
      const pct = typeof a?.allocation_percentage === 'number' ? a?.allocation_percentage : Number(a?.allocation_percentage);
      return sum + (isFinite(pct) ? (Math.max(0, pct) / 100) * size : 0);
    }, 0);
    const pctOfSize = (totalVal / size) * 100;
    return Math.abs(pctOfSize - 100) < EPSILON_PERCENT && totalVal > 0;
  }

  // Fallback: percentage-based
  const allocs = p.portfolio_allocations.filter(a => a && a.allocation_percentage != null && a.allocation_percentage !== '');
  if (allocs.length === 0) return false;
  const totalPct = allocs.reduce((sum, a) => {
    const n = typeof a.allocation_percentage === 'number' ? a.allocation_percentage : Number(a.allocation_percentage);
    return sum + (isFinite(n) ? Math.max(0, n) : 0);
  }, 0);
  const hasAnyPositive = allocs.some(a => {
    const n = typeof a.allocation_percentage === 'number' ? a.allocation_percentage : Number(a.allocation_percentage);
    return (isFinite(n) ? n : 0) > 0;
  });
  return hasAnyPositive && Math.abs(totalPct - 100) < EPSILON_PERCENT;
}
